import { useState, type FormEvent } from 'react';
import type { Camera, CameraTagWithCount, CameraTemplate } from '../lib/api';
import { TagPicker } from './CameraTags';
import { PilihOrang, type OrangPilihan } from './PilihOrang';
import { useBahasa } from '../i18n';
import { RETENSI_MAX_HARI } from '../lib/retensi';

export interface CameraDraft {
  name: string;
  host: string;
  port: number;
  httpPort: number;
  httpSecure: boolean;
  transport: 'tcp' | 'udp';
  mainPath: string;
  subPath: string | null;
  username: string | null;
  password?: string | null;
  audioEnabled: boolean;
  recordEnabled: boolean;
  /** §D6 — `null` = ikut retensi global. Bukan nol hari. */
  maxDay: number | null;
  segmentSeconds: number | null;
  isEnabled: boolean;
  /** Catatan bebas (§G11). `null` = belum ada, dan itu beda dari string kosong. */
  remarks: string | null;
  /**
   * Tag kamera ini (§G11.1). Selalu dikirim dari form ini — form yang
   * menampilkan pilihan tag lalu tidak mengirimkannya membuat pencabutan tag
   * terakhir diam-diam tidak tersimpan, dan itu keadaan yang paling sulit
   * dipercaya orang saat melihatnya.
   */
  tagIds: string[];
  /**
   * Template yang dipakai (§G11.4) — dikirim **hanya untuk kredensialnya**.
   * Field lain sudah terisi di layar dan boleh diubah sebelum disimpan;
   * password tidak pernah dikirim ke klien (§F5), jadi yang menyeberang cuma
   * id-nya dan server yang menyalin blob terenkripsinya.
   */
  templateId?: string | null;
}

const EMPTY: CameraDraft = {
  name: '',
  host: '',
  port: 554,
  httpPort: 80,
  httpSecure: false,
  transport: 'tcp',
  mainPath: '',
  subPath: null,
  username: null,
  audioEnabled: false,
  recordEnabled: true,
  // Kamera baru ikut kebijakan armada (§D6). Menyetel sendiri itu pengecualian,
  // dan pengecualian tidak pantas jadi bawaan.
  maxDay: null,
  segmentSeconds: null,
  isEnabled: true,
  remarks: null,
  tagIds: [],
};

function Field({
  label,
  hint,
  peringatan,
  children,
}: {
  label: string;
  hint?: string;
  /**
   * Keberatan yang membuat form ini tidak bisa disimpan. Dipisah dari `hint`
   * karena keduanya dibaca dengan cara yang berbeda: `hint` menjelaskan kolom
   * yang sedang benar, `peringatan` menyebutkan kolom yang sedang salah — dan
   * yang kedua harus terlihat tanpa dicari.
   */
  peringatan?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
      {peringatan && (
        <span role="alert" className="mt-1 block text-xs text-red-600">
          {peringatan}
        </span>
      )}
    </label>
  );
}

const PERINGATAN_PRIVAT =
  'Making this camera private removes it from all dashboards and from other people’s monitors, and hides other people’s exports and bookmarks of it. Afterwards only its owners can see its picture.';

const inputClass =
  'mt-1 min-h-tap w-full rounded-md border border-line bg-page px-3 text-base text-content';

export function CameraForm({
  camera,
  busy,
  retentionDefaultDays,
  segmentDefaultSeconds,
  tags,
  templates,
  kelolaPrivat,
  error,
  onSubmit,
  onCancel,
}: {
  camera: Camera | null;
  busy: boolean;
  /** Umur bawaan yang berlaku sekarang (§D6) — untuk menjelaskan kolom kosong. */
  retentionDefaultDays: number;
  /** §D2 — panjang segmen bawaan, untuk menjelaskan kotak yang dikosongkan. */
  segmentDefaultSeconds: number;
  /** Tag yang tersedia (§G11.1). Kosong = belum ada yang dibuat. */
  tags: CameraTagWithCount[];
  /** Template yang tersedia (§G11.4). Hanya ditawarkan untuk kamera baru. */
  templates: CameraTemplate[];
  /** Hanya untuk pengelola kamera privat, dan hanya saat menyunting. */
  kelolaPrivat?: { orang: OrangPilihan[] };
  /**
   * Galat simpan terakhir. Ditampilkan di form sendiri: banner halaman tidak
   * dirender selama form terbuka, dan galat yang tidak terlihat terbaca sebagai
   * "cuma kedip, entah tersimpan entah tidak" (Zaki, 23 Okt 2026).
   */
  error?: string | null;
  /** `pemilik` hanya terisi kalau daftar pemilik kamera privat diubah (`[]` = jadi kamera biasa). */
  onSubmit: (draft: CameraDraft, pemilik?: string[]) => void;
  onCancel: () => void;
}) {
  const { t } = useBahasa();
  const [draft, setDraft] = useState<CameraDraft>(() =>
    camera
      ? {
          name: camera.name,
          host: camera.host ?? '',
          port: camera.port,
          httpPort: camera.httpPort,
          httpSecure: camera.httpSecure,
          transport: camera.transport,
          mainPath: camera.mainPath,
          subPath: camera.subPath,
          username: camera.username,
          audioEnabled: camera.audioEnabled,
          recordEnabled: camera.recordEnabled,
          maxDay: camera.maxDay,
          segmentSeconds: camera.segmentSeconds,
          isEnabled: camera.isEnabled,
          remarks: camera.remarks,
          tagIds: camera.tags.map((tag) => tag.id),
        }
      : EMPTY,
  );
  const [password, setPassword] = useState('');
  const [touchedPassword, setTouchedPassword] = useState(false);
  const [templateId, setTemplateId] = useState<string>('');
  const pemilikAsal = camera?.private?.owners.map((o) => o.id) ?? [];
  const [pemilik, setPemilik] = useState<string[]>(pemilikAsal);
  const pemilikBerubah =
    !!kelolaPrivat &&
    (pemilik.length !== pemilikAsal.length || pemilik.some((id) => !pemilikAsal.includes(id)));

  /**
   * Menerapkan template **mengisi form**, tidak menguncinya (§G11.4).
   *
   * Yang perlu diubah untuk satu kamera diubah di situ juga, dan itu memang
   * biasa — satu unit dari batch yang sama sering punya substream di path lain.
   * Hanya kunci yang ADA di template yang disentuh; sisanya dibiarkan seperti
   * yang sudah diketik orangnya, termasuk nama dan host yang memang tidak
   * pernah boleh ditemplatkan.
   */
  function terapkanTemplate(id: string) {
    setTemplateId(id);
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    setDraft((d) => ({ ...d, ...tpl.values }));
  }

  const set = <K extends keyof CameraDraft>(k: K, v: CameraDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  /**
   * §D6 — batasnya juga ditegakkan server, dan itu yang menentukan. Yang
   * dikerjakan di sini cuma mendahuluinya: `max` pada kotak angkanya sudah
   * membuat browser menolak Simpan, tapi tolakan bawaan browser muncul sebagai
   * gelembung yang hilang sendiri dan tidak pernah menyebutkan **kenapa**
   * angkanya ditolak. Kalimatnya ditulis di layar, dan tombolnya ikut mati.
   */
  const retensiKelewatan = draft.maxDay !== null && draft.maxDay > RETENSI_MAX_HARI;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (retensiKelewatan) return;
    // Password hanya ikut terkirim kalau field-nya benar-benar disentuh.
    // Tanpa ini, menyimpan form edit tanpa mengisi password akan menghapus
    // kredensial kamera yang sudah tersimpan.
    const denganTemplate = { ...draft, templateId: templateId || null };
    onSubmit(
      touchedPassword ? { ...denganTemplate, password: password || null } : denganTemplate,
      // Satu tombol Save untuk semuanya: tombol simpan kedua khusus pemilik
      // ternyata dilewati orang (23 Okt 2026).
      pemilikBerubah ? pemilik : undefined,
    );
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-line bg-surface p-4">
      <h2 className="text-lg">{camera ? `Edit ${camera.name}` : 'New camera'}</h2>

      {/* Hanya untuk kamera baru. Menawarkannya saat menyunting berarti
          menawarkan menimpa setelan kamera yang sudah berjalan dari sebuah
          layar yang orangnya buka untuk mengubah satu field — dan yang memang
          ingin memberlakukan ulang punya jalannya sendiri: Bulk Operation →
          Terapkan template (§G11.2), perbuatan yang disengaja dan
          dikonfirmasi. */}
      {!camera && templates.length > 0 && (
        <div className="mt-4 rounded-md border border-line bg-subtle p-3">
          <label className="block text-sm">
            {t('Start from a template')}
            <select
              value={templateId}
              onChange={(e) => terapkanTemplate(e.target.value)}
              className={inputClass}
            >
              <option value="">{t('None — fill in everything by hand')}</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                  {tpl.hasPassword ? ` — ${t('includes credentials')}` : ''}
                </option>
              ))}
            </select>
          </label>
          <p className="mt-1 text-xs text-muted">
            {t('The template fills the fields below. Change anything you need before saving.')}
          </p>
        </div>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label={t('Name')}>
          <input className={inputClass} required value={draft.name}
            onChange={(e) => set('name', e.target.value)} />
        </Field>

        <Field label={t('Host')} hint={t('IPv4, IPv6, or hostname')}>
          <input className={inputClass} required value={draft.host}
            onChange={(e) => set('host', e.target.value)} />
        </Field>

        <Field label={t('RTSP port')}>
          <input className={inputClass} type="number" min={1} max={65535} value={draft.port}
            onChange={(e) => set('port', Number(e.target.value))} />
        </Field>

        {/* Port web UI terpisah dari port RTSP (§G11) — hampir semua kamera
            menyajikan keduanya di port yang berbeda, dan tombol "Camera
            settings" di daftar kamera memakai yang ini. */}
        <Field label={t('Web UI port')} hint={t('Used by the “Camera settings” button.')}>
          <input className={inputClass} type="number" min={1} max={65535} value={draft.httpPort}
            onChange={(e) => set('httpPort', Number(e.target.value))} />
        </Field>

        <Field label={t('Transport')}>
          <select className={inputClass} value={draft.transport}
            onChange={(e) => set('transport', e.target.value as 'tcp' | 'udp')}>
            <option value="tcp">{t('RTSP over TCP')}</option>
            <option value="udp">{t('RTSP over UDP')}</option>
          </select>
        </Field>

        <Field label={t('Main stream path')} hint={t('e.g. /Streaming/Channels/101')}>
          <input className={inputClass} required value={draft.mainPath}
            onChange={(e) => set('mainPath', e.target.value)} />
        </Field>

        <Field label={t('Sub-stream path')} hint={t('e.g. /channel=1&stream=1.sdp?')}>
          <input className={inputClass} value={draft.subPath ?? ''}
            onChange={(e) => set('subPath', e.target.value || null)} />
        </Field>

        <Field label={t('Camera username')}>
          <input className={inputClass} autoComplete="off" value={draft.username ?? ''}
            onChange={(e) => set('username', e.target.value || null)} />
        </Field>

        <Field
          label={t('Camera password')}
          hint={
            camera?.hasPassword && !touchedPassword
              ? t('Saved. Leave empty to keep it unchanged.')
              : t('Stored encrypted, and never shown again.')
          }
        >
          <input className={inputClass} type="password" autoComplete="new-password" value={password}
            onChange={(e) => { setPassword(e.target.value); setTouchedPassword(true); }} />
        </Field>

        {/* Mode kamera (§D2). Radio, bukan checkbox "Record": dua pilihan yang
            keduanya bernama membuat "View Only" jadi keadaan yang dipilih
            dengan sadar, bukan akibat sampingan dari kotak yang tidak dicentang.

            Ia juga dinaikkan ke atas pengaturan retensi dan segmen, karena
            keduanya tidak berlaku sama sekali kalau modenya View Only. */}
        <Field
          label={t('Mode')}
          hint={
            draft.recordEnabled
              ? t('Live view plus continuous recording to disk.')
              : t(
                  'Live view only — nothing is written to disk, and retention below does not apply.',
                )
          }
        >
          <div className="mt-1 flex flex-col gap-1">
            <label className="flex min-h-tap items-center gap-2 text-sm">
              <input
                type="radio"
                name="record-mode"
                checked={draft.recordEnabled}
                onChange={() => set('recordEnabled', true)}
              />
              {t('View and Record')}
            </label>
            <label className="flex min-h-tap items-center gap-2 text-sm">
              <input
                type="radio"
                name="record-mode"
                checked={!draft.recordEnabled}
                onChange={() => set('recordEnabled', false)}
              />
              {t('View Only')}
            </label>
          </div>
        </Field>

        {/* Nilainya tetap tersimpan saat View Only — mengembalikan kamera ke
            View and Record tidak boleh sekalian mengarang ulang retensinya.
            Yang berubah cuma tampilannya: isian yang tidak berpengaruh sekarang
            terlihat memang tidak berpengaruh sekarang. */}
        {/* Kosong = ikut retensi global (§D6). Placeholder-nya menyebut angka
            yang sedang berlaku, karena "ikut yang di atasnya" tanpa menyebut
            yang di atasnya berapa bukan penjelasan.

            `value` sengaja string, bukan number: `value={0}` dan
            `value={null}` sama-sama merusak kotak angka yang boleh kosong —
            yang pertama menampilkan nol yang tidak pernah diketik siapa pun,
            yang kedua membuat React menganggap input-nya tak terkendali. */}
        <Field
          label={t('Retention (days)')}
          hint={
            (draft.maxDay === null
              ? t('Following the global setting — {n} days.', { n: retentionDefaultDays })
              : t('Clear the box to follow the global setting instead.')) +
            ' ' +
            t('At most {n} days — recordings older than that are removed by the storage server itself.', {
              n: RETENSI_MAX_HARI,
            })
          }
          peringatan={retensiKelewatan ? t('{n} days is the maximum. Anything longer cannot be kept.', { n: RETENSI_MAX_HARI }) : undefined}
        >
          <input className={`${inputClass} ${draft.recordEnabled ? '' : 'text-muted'}`}
            type="number" min={1} max={RETENSI_MAX_HARI}
            value={draft.maxDay === null ? '' : draft.maxDay}
            placeholder={t('{n} (global)', { n: retentionDefaultDays })}
            disabled={!draft.recordEnabled}
            aria-invalid={retensiKelewatan || undefined}
            onChange={(e) =>
              set('maxDay', e.target.value === '' ? null : Number(e.target.value))
            } />
        </Field>

        {/* Aturannya sama persis dengan retensi di atas — termasuk kotak yang
            boleh kosong berarti "ikut yang di atasnya" (§D2). Dua setelan yang
            berperilaku sama harus terlihat sama juga; kalau tidak, yang satu
            akan dibaca dengan aturan yang satunya. */}
        <Field
          label={t('Segment length (minutes)')}
          hint={
            draft.segmentSeconds === null
              ? t('Following the global setting — {n} minutes.', {
                  n: Math.round(segmentDefaultSeconds / 60),
                })
              : t('Clear the box to follow the global setting instead.')
          }
        >
          <input className={`${inputClass} ${draft.recordEnabled ? '' : 'text-muted'}`}
            type="number" min={1} max={60}
            value={draft.segmentSeconds === null ? '' : Math.round(draft.segmentSeconds / 60)}
            placeholder={t('{n} (global)', { n: Math.round(segmentDefaultSeconds / 60) })}
            disabled={!draft.recordEnabled}
            onChange={(e) =>
              set('segmentSeconds', e.target.value === '' ? null : Number(e.target.value) * 60)
            } />
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap gap-4">
        <label className="flex min-h-tap items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.httpSecure}
            onChange={(e) => set('httpSecure', e.target.checked)} />
          {t('Web UI uses HTTPS')}
        </label>
        <label className={`flex min-h-tap items-center gap-2 text-sm ${draft.recordEnabled ? '' : 'text-muted'}`}>
          <input type="checkbox" checked={draft.audioEnabled} disabled={!draft.recordEnabled}
            onChange={(e) => set('audioEnabled', e.target.checked)} />
          {t('Record audio')}
        </label>
        <label className="flex min-h-tap items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.isEnabled}
            onChange={(e) => set('isEnabled', e.target.checked)} />
          {t('Enabled')}
        </label>
      </div>

      {/* Catatan bebas (§G11, 8 Sep 2026).

          Ditaruh di bawah — sesudah seluruh field yang menentukan apakah
          kameranya jalan — dan bukan di sebelah nama: yang mengisinya orang
          yang sudah selesai memasang, sedangkan yang membuka form ini setiap
          hari sedang mengejar host atau path. Field panjang di atas berarti
          semua orang menggulung melewatinya setiap kali.

          `textarea`, bukan `input`: yang ditulis di sini kalimat, sering lebih
          dari satu, dan kotak satu baris yang menggulung ke samping membuat
          catatan lama tidak bisa dibaca ulang tanpa diseret. */}
      <div className="mt-6">
        <label className="block">
          <span className="block text-sm">{t('Remarks')}</span>
          <p className="mb-2 mt-1 text-xs text-muted">
            {t('Notes about this camera that stay — wiring, quirks, who owns it. Shown on the camera list.')}
          </p>
          <textarea
            value={draft.remarks ?? ''}
            maxLength={2000}
            rows={3}
            onChange={(e) => set('remarks', e.target.value === '' ? null : e.target.value)}
            className={`${inputClass} min-h-[5.5rem] resize-y`}
          />
        </label>
      </div>

      <div className="mt-6">
        <span className="block text-sm">{t('Tags')}</span>
        <p className="mb-2 mt-1 text-xs text-muted">
          {t('Grouping only — tags never change who can see this camera.')}
        </p>
        <TagPicker
          tags={tags}
          terpilih={draft.tagIds}
          onChange={(ids) => set('tagIds', ids)}
        />
      </div>

      {/* Bentuknya sama dengan Tags di atas — tanpa kotak berlatar sendiri: di
          atas latar `bg-subtle` garis tombolnya hilang dan "Add" terbaca
          seperti teks biasa (Zaki, 23 Okt 2026). */}
      {camera && kelolaPrivat && (
        <div className="mt-2">
          <PilihOrang
            legend={t('Private camera')}
            hint={t('Only the owners can see this camera’s picture, live or recorded. Remove every owner to make it a regular camera again.')}
            semua={kelolaPrivat.orang}
            terpilih={pemilik}
            onChange={setPemilik}
            empty={t('No users.')}
            kosong={t('Not private — a regular camera.')}
            tambah={t('Add owner…')}
          />
          {pemilikBerubah && pemilik.length > 0 && pemilikAsal.length === 0 && (
            <p role="alert" className="mt-2 text-xs text-amber-500">{t(PERINGATAN_PRIVAT)}</p>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-6 rounded-md border border-red-600 px-3 py-2 text-sm">
          {error}
        </p>
      )}

      <div className="mt-6 flex gap-2">
        <button type="submit" disabled={busy || retensiKelewatan}
          className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-60">
          {busy ? t('Saving…') : t('Save')}
        </button>
        <button type="button" onClick={onCancel}
          className="min-h-tap rounded-md border border-line px-4 text-base">
          {t('Cancel')}
        </button>
      </div>
    </form>
  );
}
